import React from "react";
import { Search, ChevronDown, XCircle } from "lucide-react";
import { Card, CardContent, Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { MAPPING_REVIEW_STATUS_OPTIONS } from "@/lib/mappingReview/constants";
import type { MappingReviewFilterBarProps } from "./MappingReviewFilterBar.types";

export const MappingReviewFilterBar: React.FC<MappingReviewFilterBarProps> = ({
  searchQuery,
  onSearchChange,
  selectedMarketplace,
  onMarketplaceChange,
  marketplaces,
  selectedStatus,
  onStatusChange,
  hasActiveFilters,
  onResetFilters,
}) => {
  return (
    <Card className="mb-6">
      <CardContent className="py-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="mapping-search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search by product type..."
              className={cn(
                "w-full pl-10 pr-4 py-2 text-sm rounded-lg",
                "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700",
                "text-slate-900 dark:text-slate-100",
                "placeholder:text-slate-400 dark:placeholder:text-slate-500",
                "focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500",
                "transition-colors",
              )}
            />
          </div>
          <div className="relative">
            <select
              data-testid="marketplace-filter"
              value={selectedMarketplace}
              onChange={(e) => onMarketplaceChange(e.target.value)}
              className={cn(
                "appearance-none pl-3 pr-8 py-2 text-sm rounded-lg",
                "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700",
                "text-slate-900 dark:text-slate-100",
                "focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500",
                "cursor-pointer",
              )}
            >
              <option value="all">All Marketplaces</option>
              {marketplaces.slice(1).map((mp) => (
                <option key={mp} value={mp}>
                  {mp}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
          <div className="relative">
            <select
              data-testid="status-filter"
              value={selectedStatus}
              onChange={(e) => onStatusChange(e.target.value)}
              className={cn(
                "appearance-none pl-3 pr-8 py-2 text-sm rounded-lg",
                "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700",
                "text-slate-900 dark:text-slate-100",
                "focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500",
                "cursor-pointer",
              )}
            >
              <option value="all">All Statuses</option>
              {MAPPING_REVIEW_STATUS_OPTIONS.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={onResetFilters}>
              <XCircle className="w-4 h-4" />
              Reset
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
